import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { apiService } from '@/services/api';

/**
 * Custom hook for managing bookings with search, filter, sort, and pagination
 *
 * UPDATED: Now uses apiService instead of direct axios calls
 * - Removed redundant auth headers (apiService handles this automatically)
 * - Added request cancellation for fetchBookings
 * - Cleaner error handling
 */
export const useBookings = (API) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFilter, setSearchFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all', 'local', 'beds24'
  const [statusFilter, setStatusFilter] = useState('active'); // 'active', 'cancelled'

  // Sorting state
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);

  /**
   * Fetch bookings with search parameters
   * Now includes request cancellation
   */
  const fetchBookings = useCallback(async () => {
    // Create abort controller for request cancellation
    const controller = new AbortController();

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (searchQuery) {
        params.append('search', searchQuery);
        params.append('filter', searchFilter);
      }

      const url = `/admin/bookings${params.toString() ? '?' + params.toString() : ''}`;

      // Use apiService instead of axios directly
      const response = await apiService.get(url, {
        signal: controller.signal
      });

      setBookings(response.data);
    } catch (err) {
      // Don't show error if request was cancelled
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }

      setError(err);
      toast.error('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }

    // Return cleanup function
    return () => controller.abort();
  }, [searchQuery, searchFilter]);

  /**
   * Create booking
   */
  const createBooking = useCallback(async (bookingData) => {
    try {
      // Use apiService instead of axios
      await apiService.post('/admin/bookings', bookingData);
      toast.success('Booking created successfully');
      await fetchBookings();
      return { success: true };
    } catch (err) {
      const errorMsg = err.response?.data?.detail
        ? (typeof err.response.data.detail === 'string'
            ? err.response.data.detail
            : JSON.stringify(err.response.data.detail))
        : 'Failed to create booking';
      toast.error(errorMsg);
      return { success: false, error: err };
    }
  }, [fetchBookings]);

  /**
   * Update booking
   */
  const updateBooking = useCallback(async (bookingId, bookingData) => {
    try {
      await apiService.put(`/admin/bookings/${bookingId}`, bookingData);
      toast.success('Booking updated successfully');
      await fetchBookings();
      return { success: true };
    } catch (err) {
      const errorMsg = err.response?.data?.detail
        ? (typeof err.response.data.detail === 'string'
            ? err.response.data.detail
            : JSON.stringify(err.response.data.detail))
        : 'Failed to update booking';
      toast.error(errorMsg);
      return { success: false, error: err };
    }
  }, [fetchBookings]);

  /**
   * Delete booking
   */
  const deleteBooking = useCallback(async (bookingId) => {
    try {
      await apiService.delete(`/admin/bookings/${bookingId}`);
      toast.success('Booking deleted successfully');
      await fetchBookings();
      return { success: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete booking');
      return { success: false, error: err };
    }
  }, [fetchBookings]);

  /**
   * Update payment status
   */
  const updatePaymentStatus = useCallback(async (bookingId, newStatus) => {
    try {
      await apiService.patch(`/admin/bookings/${bookingId}/payment-status?payment_status=${newStatus}`);
      toast.success('Payment status updated');
      await fetchBookings();
      return { success: true };
    } catch (err) {
      toast.error('Failed to update payment status');
      return { success: false, error: err };
    }
  }, [fetchBookings]);

  /**
   * Clear all bookings
   * FIXED: Removed redundant Authorization header (apiService adds it automatically)
   */
  const clearAllBookings = useCallback(async () => {
    try {
      // No need to manually add auth header anymore!
      const response = await apiService.delete('/admin/bookings/clear/all');
      toast.success(`✅ ${response.data.message}`);
      await fetchBookings();
      return { success: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear bookings');
      return { success: false, error: err };
    }
  }, [fetchBookings]);

  /**
   * Filtered bookings by source (memoized for performance)
   */
  const filteredBySource = useMemo(() => {
    let filtered = bookings;
    
    // Filter by status (active vs cancelled)
    if (statusFilter === 'active') {
      filtered = filtered.filter(b => b.status !== 'cancelled' && b.payment_status !== 'cancelled');
    } else if (statusFilter === 'cancelled') {
      filtered = filtered.filter(b => b.status === 'cancelled' || b.payment_status === 'cancelled');
    }
    
    // Filter by source
    if (sourceFilter === 'all') return filtered;
    
    return filtered.filter(booking => {
      const isLocalBooking = booking.id && booking.id.startsWith('BK');
      if (sourceFilter === 'local') return isLocalBooking;
      if (sourceFilter === 'beds24') return !isLocalBooking;
      return true;
    });
  }, [bookings, sourceFilter, statusFilter]);

  /**
   * Sorted bookings (memoized for performance)
   */
  const sortedBookings = useMemo(() => {
    return [...filteredBySource].sort((a, b) => {
      let aVal, bVal;

      switch(sortField) {
        case 'guest_name':
          aVal = a.guest_name?.toLowerCase() || '';
          bVal = b.guest_name?.toLowerCase() || '';
          break;
        case 'check_in':
          aVal = new Date(a.check_in);
          bVal = new Date(b.check_in);
          break;
        case 'total_amount':
          aVal = a.total_amount || 0;
          bVal = b.total_amount || 0;
          break;
        case 'payment_status':
          aVal = a.payment_status || '';
          bVal = b.payment_status || '';
          break;
        case 'created_at':
        default:
          aVal = new Date(a.created_at || 0);
          bVal = new Date(b.created_at || 0);
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredBySource, sortField, sortOrder]);

  /**
   * Paginated bookings (memoized for performance)
   */
  const paginatedBookings = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedBookings.slice(startIndex, endIndex);
  }, [sortedBookings, currentPage, itemsPerPage]);

  /**
   * Total pages calculation
   */
  const totalPages = useMemo(() => {
    return Math.ceil(filteredBySource.length / itemsPerPage);
  }, [filteredBySource.length, itemsPerPage]);

  /**
   * Toggle sort
   */
  const toggleSort = useCallback((field) => {
    if (sortField === field) {
      setSortOrder(order => order === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  }, [sortField]);

  /**
   * Reset page when search or source filter changes
   */
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, searchFilter, sourceFilter, statusFilter]);

  /**
   * Fetch bookings on mount and when search changes
   * Now properly cleans up on unmount
   */
  useEffect(() => {
    const cleanup = fetchBookings();
    return () => {
      // Cancel ongoing request when component unmounts
      if (cleanup && typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, [fetchBookings]);

  return {
    // Data
    bookings,
    filteredBySource,
    paginatedBookings,
    loading,
    error,

    // Search & Filter
    searchQuery,
    setSearchQuery,
    searchFilter,
    setSearchFilter,
    sourceFilter,
    setSourceFilter,
    statusFilter,
    setStatusFilter,

    // Sorting
    sortField,
    sortOrder,
    toggleSort,

    // Pagination
    currentPage,
    setCurrentPage,
    itemsPerPage,
    totalPages,

    // Operations
    fetchBookings,
    createBooking,
    updateBooking,
    deleteBooking,
    updatePaymentStatus,
    clearAllBookings,
  };
};
